import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Download, Users, AlertTriangle, Bookmark, BarChart2, Trash2, Plus, ExternalLink } from 'lucide-react';

export default function ReportsPage() {
  const { user, login } = useAuth();

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [uncertain, setUncertain] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [tab, setTab] = useState('sessions');
  const [loading, setLoading] = useState(false);

  // Baseline management state
  const [showAddBaseline, setShowAddBaseline] = useState(false);
  const [blKey, setBlKey] = useState('');
  const [blSummary, setBlSummary] = useState('');
  const [blPoints, setBlPoints] = useState('');
  const [blNotes, setBlNotes] = useState('');
  const [blAdding, setBlAdding] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.teams.list().then(setTeams).catch(console.error);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const teamId = selectedTeam?.id;

    Promise.all([
      api.reports.sessions(teamId ? { team_id: teamId } : {}),
      api.reports.attendance(selectedTeam ? { team_name: selectedTeam.name } : {}),
      api.reports.uncertain(teamId),
      teamId ? api.teams.baselines(teamId) : Promise.resolve([])
    ])
      .then(([s, a, u, b]) => {
        setSessions(s);
        setAttendance(a);
        setUncertain(u);
        setBaselines(b);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user, selectedTeam]);

  const handleAddBaseline = async () => {
    if (!selectedTeam || !blKey || !blPoints) return;
    setBlAdding(true);
    try {
      const newB = await api.teams.addBaseline(selectedTeam.id, {
        jira_ticket_key: blKey.trim().toUpperCase(),
        jira_ticket_summary: blSummary.trim(),
        points: parseInt(blPoints),
        notes: blNotes.trim()
      });
      setBaselines(bs => [newB, ...bs]);
      setBlKey(''); setBlSummary(''); setBlPoints(''); setBlNotes('');
      setShowAddBaseline(false);
    } catch (e) {
      console.error(e);
    } finally {
      setBlAdding(false);
    }
  };

  const handleMarkReviewed = async (sessionId, roundId) => {
    try {
      await api.sessions.markReviewed(sessionId, roundId);
      setUncertain(prev =>
        prev.map(t =>
          t.round_id === roundId
            ? { ...t, reestimate_reviewed_at: new Date().toISOString() }
            : t
        )
      );
    } catch (e) {
      console.error('[FEAT-07] markReviewed failed:', e.message);
    }
  };

  const handleDeleteBaseline = async (teamId, baselineId) => {
    if (!window.confirm('Remove this baseline?')) return;
    try {
      await api.teams.deleteBaseline(teamId, baselineId);
      setBaselines(bs => bs.filter(b => b.id !== baselineId));
    } catch (e) { console.error(e); }
  };

  if (!user) {
    return (
      <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div className="card" style={{ textAlign: 'center', padding: '48px 32px', maxWidth: 400 }}>
          <BarChart2 size={40} style={{ color: 'var(--accent)', marginBottom: 16 }} />
          <h2 style={{ marginBottom: 8 }}>Reports</h2>
          <p className="text-muted mb-6">Sign in to view session history and reports.</p>
          <button className="btn btn-primary" onClick={login}>Sign in with Jira</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'sessions', label: 'Sessions', count: sessions.length },
    { id: 'attendance', label: 'Attendance', count: attendance.length },
    { id: 'uncertain', label: '⚠ Re-estimate', count: uncertain.length },
    { id: 'baselines', label: '⚓ Baselines', count: baselines.length }
  ];

  const POINT_VALUES = ['1', '2', '3', '5', '8'];

  return (
    <div className="page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ marginBottom: 4 }}>Reports</h1>
          <p className="text-muted">History, attendance, and estimation insights</p>
        </div>
        <select className="input" style={{ width: 200 }} value={selectedTeam?.id || ''}
          onChange={e => { const t = teams.find(t => t.id === e.target.value); setSelectedTeam(t || null); }}>
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {/* Stats */}
      <div className="grid-3 mb-6">
        <StatCard label="Sessions" value={sessions.length} icon={<BarChart2 size={20} />} color="var(--accent)" />
        <StatCard label="Re-estimate Tickets" value={uncertain.length} icon={<AlertTriangle size={20} />} color="var(--yellow)" />
        <StatCard label="Unique Participants" value={new Set(attendance.map(a => a.display_name)).size} icon={<Users size={20} />} color="var(--green)" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6" style={{ borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.id} className="btn btn-ghost"
            style={{
              borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
              paddingBottom: 12
            }}
            onClick={() => setTab(t.id)}>
            {t.label}
            {t.count > 0 && <span className="badge badge-muted" style={{ marginLeft: 6 }}>{t.count}</span>}
          </button>
        ))}

        {/* Export buttons */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {tab === 'attendance' && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => api.reports.downloadAttendanceCsv(selectedTeam?.name)}>
                <Download size={12} /> CSV
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => api.reports.downloadAttendancePdf(selectedTeam?.name)}>
                <Download size={12} /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><p className="text-muted pulse">Loading...</p></div>
      ) : (
        <>
          {tab === 'sessions' && <SessionsTab sessions={sessions} />}
          {tab === 'attendance' && <AttendanceTab attendance={attendance} />}
          {tab === 'uncertain' && <UncertainTab tickets={uncertain} cloudName={user?.cloudName} onMarkReviewed={handleMarkReviewed} />}
          {tab === 'baselines' && (
            <BaselinesTab
              baselines={baselines}
              selectedTeam={selectedTeam}
              showAdd={showAddBaseline}
              setShowAdd={setShowAddBaseline}
              blKey={blKey} setBlKey={setBlKey}
              blSummary={blSummary} setBlSummary={setBlSummary}
              blPoints={blPoints} setBlPoints={setBlPoints}
              blNotes={blNotes} setBlNotes={setBlNotes}
              adding={blAdding}
              onAdd={handleAddBaseline}
              onDelete={handleDeleteBaseline}
              cloudName={user?.cloudName}
              pointValues={POINT_VALUES}
            />
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div className="card flex items-center gap-4">
      <div style={{
        width: 48, height: 48, borderRadius: 12,
        background: `color-mix(in srgb, ${color} 15%, transparent)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color, flexShrink: 0
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>{value}</div>
        <div className="text-sm text-muted">{label}</div>
      </div>
    </div>
  );
}

function SessionsTab({ sessions }) {
  if (sessions.length === 0) return <EmptyState icon="📊" text="No sessions recorded yet." />;
  return (
    <div className="flex-col gap-3">
      {sessions.map(s => (
        <div key={s.session_id} className="card flex items-center gap-4" style={{ padding: '16px 20px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold truncate">{s.session_name}</span>
              <span className={`badge ${s.status === 'active' ? 'badge-green' : 'badge-muted'}`}>{s.status}</span>
            </div>
            <div className="text-sm text-muted">
              {s.team_name} · {s.facilitator_name} · {new Date(s.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="flex gap-3 text-sm">
            <div style={{ textAlign: 'center' }}>
              <div className="font-bold">{s.tickets_estimated || 0}</div>
              <div className="text-xs text-muted">tickets</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="font-bold">{s.voter_count || 0}</div>
              <div className="text-xs text-muted">voters</div>
            </div>
            {s.uncertain_count > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div className="font-bold text-yellow">{s.uncertain_count}</div>
                <div className="text-xs text-muted">uncertain</div>
              </div>
            )}
          </div>
          <div className="flex gap-1">
            <button className="btn btn-ghost btn-sm" onClick={() => api.reports.downloadSessionCsv(s.session_id)} title="Export CSV">
              <Download size={12} /> CSV
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => api.reports.downloadSessionPdf(s.session_id)} title="Export PDF">
              <Download size={12} /> PDF
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AttendanceTab({ attendance }) {
  if (attendance.length === 0) return <EmptyState icon="👥" text="No attendance data yet." />;
  const bySess = {};
  attendance.forEach(a => {
    const key = a.session_name;
    if (!bySess[key]) bySess[key] = { session: a.session_name, date: a.session_date, team: a.team_name, participants: [] };
    bySess[key].participants.push(a);
  });
  return (
    <div className="flex-col gap-4">
      {Object.values(bySess).map(sess => (
        <div key={sess.session} className="card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-bold">{sess.session}</div>
              <div className="text-sm text-muted">{sess.team} · {new Date(sess.date).toLocaleDateString()}</div>
            </div>
            <span className="badge badge-muted"><Users size={10} /> {sess.participants.length}</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {sess.participants.map((p, i) => (
              <div key={i} style={{
                background: p.is_observer ? 'var(--blue-dim)' : 'var(--bg-elevated)',
                border: `1px solid ${p.is_observer ? 'var(--blue)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: 12
              }}>
                {p.display_name}
                {p.is_observer && <span className="text-muted"> (observer)</span>}
                {p.votes_cast > 0 && <span style={{ color: 'var(--accent)', marginLeft: 4 }}>· {p.votes_cast}v</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function UncertainTab({ tickets, cloudName, onMarkReviewed }) {
  const [reviewedOpen, setReviewedOpen] = useState(false);

  const active = tickets.filter(t => !t.reestimate_reviewed_at);
  const reviewed = tickets.filter(t => !!t.reestimate_reviewed_at);

  if (tickets.length === 0) return <EmptyState icon="✅" text="No re-estimate tickets. Great job estimating!" />;

  return (
    <div>
      <div className="toast toast-info mb-4">
        <AlertTriangle size={14} />
        These tickets were marked uncertain and have a <strong>'re-estimate'</strong> label in Jira.
        Review during your next retro — confirm or dispute the estimate with the team.
      </div>

      <div className="flex-col gap-3">
        {active.map(t => (
          <div key={t.round_id} className="card flex items-center gap-4" style={{ padding: '14px 20px' }}>
            <span className="badge badge-yellow font-mono">{t.jira_ticket_key}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="text-sm truncate">{t.jira_ticket_summary}</div>
              <div className="text-xs text-muted">{t.team_name} · {new Date(t.completed_at).toLocaleDateString()}</div>
            </div>
            <span className="badge badge-purple">{t.final_points} pts</span>
            <a
              href={`https://${cloudName || 'yoursite'}.atlassian.net/browse/${t.jira_ticket_key}`}
              target="_blank" rel="noopener noreferrer"
              className="btn btn-ghost btn-sm" style={{ color: 'var(--accent)' }}
            >
              <ExternalLink size={12} /> Jira
            </a>
            <button
              className="btn btn-ghost btn-sm"
              style={{ color: 'var(--green)', flexShrink: 0 }}
              onClick={() => onMarkReviewed(t.session_id, t.round_id)}
              title="Mark as reviewed — removes re-estimate label from Jira"
            >
              ✓ Mark reviewed
            </button>
          </div>
        ))}
      </div>

      {reviewed.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--text-muted)' }}
            onClick={() => setReviewedOpen(o => !o)}
          >
            {reviewedOpen ? '▲' : '▶'} Reviewed ({reviewed.length})
          </button>
          {reviewedOpen && (
            <div className="flex-col gap-3 mt-3">
              {reviewed.map(t => (
                <div key={t.round_id} className="card flex items-center gap-4"
                  style={{ padding: '14px 20px', opacity: 0.6 }}>
                  <span className="badge badge-muted font-mono">{t.jira_ticket_key}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="text-sm truncate">{t.jira_ticket_summary}</div>
                    <div className="text-xs text-muted">{t.team_name} · {new Date(t.completed_at).toLocaleDateString()}</div>
                  </div>
                  <span className="badge badge-purple">{t.final_points} pts</span>
                  <span className="badge badge-green">✓ reviewed</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BaselinesTab({ baselines, selectedTeam, showAdd, setShowAdd, blKey, setBlKey,
  blSummary, setBlSummary, blPoints, setBlPoints, blNotes, setBlNotes,
  adding, onAdd, onDelete, cloudName, pointValues }) {

  if (!selectedTeam) {
    return <EmptyState icon="⚓" text="Select a team to manage baselines." />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted">
          Baselines are reference tickets your team uses during estimation to calibrate story points.
          They persist across all sessions for this team.
        </p>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(s => !s)}>
          <Plus size={12} /> Add Baseline
        </button>
      </div>

      {showAdd && (
        <div className="card mb-4" style={{ border: '1px solid var(--accent)' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: 16 }}>Add Baseline Reference</h3>
          <div className="grid-2" style={{ gap: 12, marginBottom: 12 }}>
            <div className="input-group" style={{ marginBottom: 0 }}>
              <label className="input-label">Ticket Key *</label>
              <input className="input" value={blKey} onChange={e => setBlKey(e.target.value)} placeholder="e.g. PROJ-123" />
            </div>
            <div>
              <label className="input-label">Story Points *</label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {pointValues.map(v => (
                  <button key={v} className={`btn btn-sm ${blPoints == v ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setBlPoints(v)}>{v}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="input-group">
            <label className="input-label">Summary (optional)</label>
            <input className="input" value={blSummary} onChange={e => setBlSummary(e.target.value)} placeholder="Short description" />
          </div>
          <div className="input-group">
            <label className="input-label">Notes (optional)</label>
            <input className="input" value={blNotes} onChange={e => setBlNotes(e.target.value)}
              placeholder="e.g. 'Classic 5-pointer: straightforward auth flow'" />
          </div>
          <div className="flex gap-3 mt-2">
            <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={onAdd} disabled={adding || !blKey || !blPoints}>
              {adding ? 'Saving...' : 'Save Baseline'}
            </button>
          </div>
        </div>
      )}

      {baselines.length === 0 ? (
        <EmptyState icon="⚓" text={`No baselines for ${selectedTeam.name} yet.`} />
      ) : (
        <div className="flex-col gap-3">
          {baselines.map(b => (
            <div key={b.id} className="card flex items-center gap-4" style={{ padding: '14px 20px' }}>
              <span className="badge badge-yellow font-mono">{b.jira_ticket_key}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {b.jira_ticket_summary && <div className="text-sm font-bold truncate">{b.jira_ticket_summary}</div>}
                {b.notes && <div className="text-xs text-muted">💡 {b.notes}</div>}
                <div className="text-xs text-muted">Added {new Date(b.added_at).toLocaleDateString()}</div>
              </div>
              <span className="badge badge-purple">{b.points} pts</span>
              <a href={`https://${cloudName || 'yoursite'}.atlassian.net/browse/${b.jira_ticket_key}`}
                target="_blank" rel="noopener noreferrer"
                className="btn btn-ghost btn-icon btn-sm" title="Open in Jira">
                <ExternalLink size={14} />
              </a>
              <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--red)' }}
                onClick={() => onDelete(selectedTeam.id, b.id)} title="Remove baseline">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>{icon}</div>
      <p className="text-muted">{text}</p>
    </div>
  );
}
